<script>
  // Status page: list of monitored URLs with up/down chip, last latency,
  // last seen, recent-history sparkline. Admins can add / edit / delete
  // targets; everyone else sees a read-only view. Polls every 10s so the
  // UI tracks the server-side checker's results without manual refresh.
  import { onMount, onDestroy } from 'svelte';
  import { api } from '../lib/api.js';
  import { ui } from '../lib/ui.svelte.js';
  import { getUser } from '../lib/auth.js';
  import TextField from '../components/TextField.svelte';
  import Select from '../components/Select.svelte';
  import StatusHelp from '../components/StatusHelp.svelte';

  const me = getUser();
  const isAdmin = me?.portal_role === 'admin';

  let targets = $state([]);
  let loading = $state(true);
  let err = $state('');
  let creating = $state(false);
  let editingId = $state(null); // target id being edited, or null
  let pollTimer = null;

  // Draft, shared by the create + edit form.
  let draftName = $state('');
  let draftUrl = $state('');
  let draftMethod = $state('GET');
  let draftExpected = $state('');
  let draftInterval = $state('60');
  let draftTimeout = $state('10');
  let draftFollow = $state(true); // follow 3xx redirects until 2xx/4xx/5xx
  // Group selector: pick an existing group, "no group", or "__new__" which
  // reveals a text field for a brand-new group name.
  let draftGroupSel = $state('');
  let draftGroupNew = $state('');

  // Distinct group labels currently in use, for the create/edit dropdown.
  const existingGroups = $derived.by(() => {
    const s = new Set();
    for (const t of targets) { const g = (t.group || '').trim(); if (g) s.add(g); }
    return [...s].sort((a, b) => a.localeCompare(b));
  });
  const groupSelectOptions = $derived([
    { value: '', label: 'No group' },
    ...existingGroups.map((g) => ({ value: g, label: g })),
    { value: '__new__', label: '＋ New group…' },
  ]);

  // Targets bucketed into sections by group; ungrouped sorts last. The
  // sentinel key keeps "Ungrouped" distinct from a real group literally
  // named "Ungrouped".
  const UNGROUPED = ' ungrouped';
  const groups = $derived.by(() => {
    const map = new Map();
    for (const t of targets) {
      const key = (t.group || '').trim() || UNGROUPED;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    }
    const keys = [...map.keys()].sort((a, b) => {
      if (a === UNGROUPED) return 1;
      if (b === UNGROUPED) return -1;
      return a.localeCompare(b);
    });
    return keys.map((k) => ({ key: k, label: k === UNGROUPED ? 'Ungrouped' : k, items: map.get(k) }));
  });
  // Only show section headers once there's real grouping to show.
  const showGroupHeaders = $derived(groups.length > 1 || (groups.length === 1 && groups[0].key !== UNGROUPED));

  async function load(silent = false) {
    if (!silent) loading = true;
    err = '';
    try { targets = (await api.listStatusTargets()).targets || []; }
    catch (e) { err = e.message; }
    finally { loading = false; }
  }

  function resetDraft() {
    draftName = ''; draftUrl = ''; draftMethod = 'GET';
    draftExpected = ''; draftInterval = '60'; draftTimeout = '10';
    draftFollow = true;
    draftGroupSel = ''; draftGroupNew = '';
  }
  function openCreate() {
    editingId = null;
    resetDraft();
    creating = !creating;
  }
  function openEdit(t) {
    editingId = t.id;
    creating = false;
    draftName = t.name || '';
    draftUrl = t.url || '';
    draftMethod = t.method || 'GET';
    draftExpected = t.expected_status ? String(t.expected_status) : '';
    draftInterval = String(t.interval_seconds || 60);
    draftTimeout = String(t.timeout_seconds || 10);
    draftFollow = t.follow_redirects !== false; // default on for legacy/unset
    draftGroupSel = (t.group || '').trim();
    draftGroupNew = '';
  }
  function cancelForm() { creating = false; editingId = null; resetDraft(); }

  function fmtTime(t) { return t ? new Date(t).toLocaleString() : '—'; }
  function fmtLatency(ms) { return ms != null ? `${ms} ms` : '—'; }
  function stateOf(t) {
    if (t.last_up == null) return 'unknown';
    return t.last_up ? 'up' : 'down';
  }
  function stateClass(t) {
    const s = stateOf(t);
    if (s === 'up')   return 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300';
    if (s === 'down') return 'bg-error/15 text-error';
    return 'bg-surface-variant text-on-surface-variant';
  }
  function stateIcon(t) {
    const s = stateOf(t);
    if (s === 'up')   return 'check_circle';
    if (s === 'down') return 'error';
    return 'help';
  }
  // Renders the redirect/status chain as "302 → 200". Falls back to the
  // single last_status when no chain was recorded (older results).
  function chainText(t) {
    if (Array.isArray(t.last_chain) && t.last_chain.length > 0) {
      return t.last_chain.join(' → ');
    }
    return t.last_status ? String(t.last_status) : '—';
  }
  function hasRedirects(t) {
    return Array.isArray(t.last_chain) && t.last_chain.length > 1;
  }

  async function save() {
    if (!draftName.trim() || !draftUrl.trim()) return;
    const group = (draftGroupSel === '__new__' ? draftGroupNew : draftGroupSel).trim();
    const body = {
      name: draftName.trim(),
      url: draftUrl.trim(),
      group,
      method: draftMethod,
      expected_status: draftExpected ? Number(draftExpected) : 0,
      interval_seconds: Number(draftInterval) || 60,
      timeout_seconds: Number(draftTimeout) || 10,
      follow_redirects: draftFollow,
    };
    try {
      if (editingId) {
        await api.updateStatusTarget(editingId, body);
        ui.snack('Target updated', 'success');
      } else {
        await api.createStatusTarget({ ...body, enabled: true });
        ui.snack('Target added', 'success');
      }
      cancelForm();
      load();
    } catch (e) { ui.snack(e.message, 'error'); }
  }

  async function toggleEnabled(t) {
    try {
      await api.updateStatusTarget(t.id, { enabled: !t.enabled });
      load();
    } catch (e) { ui.snack(e.message, 'error'); }
  }

  async function remove(t) {
    const ok = await ui.ask({
      title: 'Delete target?',
      message: `Stop monitoring ${t.name} and drop its history?`,
      confirmText: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try { await api.deleteStatusTarget(t.id); ui.snack('Target deleted', 'success'); load(); }
    catch (e) { ui.snack(e.message, 'error'); }
  }

  function onKey(e) {
    if ((creating || editingId) && e.key === 'Escape') cancelForm();
  }

  onMount(() => {
    load();
    pollTimer = setInterval(() => load(true), 10_000);
  });
  onDestroy(() => { if (pollTimer) clearInterval(pollTimer); });
</script>

<svelte:window onkeydown={onKey} />

{#if isAdmin}
  <div class="flex justify-end mb-4">
    <button onclick={openCreate} class="shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary text-on-primary text-sm font-medium px-3 sm:px-5 py-2.5 sm:py-2" title="Add target">
      <span class="material-symbols-outlined text-base">add_circle</span>
      <span class="hidden sm:inline">Add target</span>
    </button>
  </div>
{/if}

<StatusHelp />

{#if (creating || editingId) && isAdmin}
  <!-- M3 dialog: scrim + centered card (28px corners), matching
       ConfirmDialog. Esc / scrim-click closes; the form submits on Enter. -->
  <div class="status-dlg-scrim" onclick={cancelForm} role="presentation"></div>
  <div class="status-dlg-wrap" role="dialog" aria-modal="true" aria-labelledby="status-dlg-title">
    <form class="status-dlg" onsubmit={(e) => { e.preventDefault(); save(); }}>
      <h2 id="status-dlg-title" class="status-dlg-title">{editingId ? 'Edit target' : 'New target'}</h2>
      <div class="status-dlg-grid">
        <div class="status-dlg-wide"><TextField label="Name *"     bind:value={draftName} /></div>
        <div class="status-dlg-wide"><TextField label="URL *"      bind:value={draftUrl} /></div>
        <div class="status-dlg-wide">
          <Select label="Group" bind:value={draftGroupSel} options={groupSelectOptions} />
        </div>
        {#if draftGroupSel === '__new__'}
          <div class="status-dlg-wide"><TextField label="New group name" bind:value={draftGroupNew} /></div>
        {/if}
        <TextField label="Method"     bind:value={draftMethod} />
        <TextField label="Expected status" bind:value={draftExpected} />
        <TextField label="Interval (seconds)" bind:value={draftInterval} />
        <TextField label="Timeout (seconds)"  bind:value={draftTimeout} />
        <p class="status-dlg-hint status-dlg-wide">Expected status — leave blank to accept any 2xx.</p>
        <label class="status-dlg-check status-dlg-wide">
          <input type="checkbox" bind:checked={draftFollow} class="status-dlg-checkbox" />
          <span>
            Follow redirects
            <span class="status-dlg-check-sub">Follow 3xx responses until a 2xx/4xx/5xx; the full chain (e.g. 302 → 200) is recorded.</span>
          </span>
        </label>
      </div>
      <div class="status-dlg-actions">
        <button type="button" onclick={cancelForm} class="status-dlg-btn">Cancel</button>
        <button type="submit" class="status-dlg-btn status-dlg-btn-primary">{editingId ? 'Save' : 'Add'}</button>
      </div>
    </form>
  </div>
{/if}

{#if loading && targets.length === 0}
  <p class="text-on-surface-variant">Loading...</p>
{:else if err}
  <p class="text-error">{err}</p>
{:else if targets.length === 0}
  <div class="bg-surface-container rounded-2xl p-12 text-center text-on-surface-variant">
    <span class="material-symbols-outlined text-5xl mb-2">monitor_heart</span>
    <p>No targets yet.</p>
    {#if isAdmin}<p class="text-sm mt-2">Click <em>Add target</em> to start monitoring a URL.</p>{/if}
  </div>
{:else}
  {#each groups as g (g.key)}
    <section class="status-group">
      {#if showGroupHeaders}
        <h2 class="status-group-head">
          <span class="status-group-name">{g.label}</span>
          <span class="status-group-count">{g.items.length}</span>
        </h2>
      {/if}
      <ul class="status-grid">
        {#each g.items as t (t.id)}
      <li class="status-card" class:disabled={!t.enabled}>
        <header class="status-card-head">
          <span class="px-2 py-0.5 rounded-md text-xs font-medium inline-flex items-center gap-1 {stateClass(t)}">
            <span class="material-symbols-outlined" style="font-size: 14px;">{stateIcon(t)}</span>
            {stateOf(t)}
          </span>
          <h3 class="status-card-name">
            <a href={t.url} target="_blank" rel="noopener" title="Open {t.url} in a new tab">{t.name}</a>
          </h3>
          {#if !t.enabled}<span class="status-card-paused">paused</span>{/if}
          {#if isAdmin}
            <div class="status-card-actions">
              <button onclick={() => openEdit(t)} class="status-icon-btn" title="Edit">
                <span class="material-symbols-outlined">edit</span>
              </button>
              <button onclick={() => toggleEnabled(t)} class="status-icon-btn" title={t.enabled ? 'Pause' : 'Resume'}>
                <span class="material-symbols-outlined">{t.enabled ? 'pause' : 'play_arrow'}</span>
              </button>
              <button onclick={() => remove(t)} class="status-icon-btn" title="Delete">
                <span class="material-symbols-outlined">delete</span>
              </button>
            </div>
          {/if}
        </header>
        <a href={t.url} target="_blank" rel="noopener" class="status-card-url">
          <span class="status-card-url-text">{t.url}</span>
          <span class="material-symbols-outlined status-card-url-icon" aria-hidden="true">open_in_new</span>
        </a>
        <dl class="status-card-meta">
          <div><dt>Latency</dt><dd>{fmtLatency(t.last_latency_ms)}</dd></div>
          <div>
            <dt>{hasRedirects(t) ? 'Status chain' : 'Last status'}</dt>
            <dd title={hasRedirects(t) ? 'Followed redirects to a final response' : ''}>{chainText(t)}</dd>
          </div>
          <div><dt>Last checked</dt><dd>{fmtTime(t.last_checked_at)}</dd></div>
          <div><dt>Every</dt><dd>{t.interval_seconds || 60}s</dd></div>
        </dl>
        {#if hasRedirects(t) && t.last_final_url && t.last_final_url !== t.url}
          <a href={t.last_final_url} target="_blank" rel="noopener" class="status-card-final" title="Final URL after redirects">
            <span class="material-symbols-outlined" style="font-size: 13px;">subdirectory_arrow_right</span>
            <span class="status-card-final-text">{t.last_final_url}</span>
          </a>
        {/if}
        {#if t.last_error}
          <p class="status-card-error">{t.last_error}</p>
        {/if}
      </li>
        {/each}
      </ul>
    </section>
  {/each}
{/if}

<style>
  /* Edit / New target dialog -- mirrors ConfirmDialog's M3 basic-dialog
     styling (28px corners, surface-container-high, scrim). */
  .status-dlg-scrim {
    position: fixed;
    inset: 0;
    background: rgb(0 0 0 / 0.32);
    z-index: 1200;
    animation: status-scrim-in 180ms ease-out;
  }
  .status-dlg-wrap {
    position: fixed;
    inset: 0;
    z-index: 1201;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    pointer-events: none;
  }
  .status-dlg {
    pointer-events: auto;
    inline-size: 100%;
    max-inline-size: 520px;
    max-block-size: calc(100dvh - 32px);
    overflow-y: auto;
    background: rgb(var(--md-sys-color-surface-container-high));
    /* Fields inside notch against this surface. */
    --md-tf-surface: var(--md-sys-color-surface-container-high);
    color: rgb(var(--md-sys-color-on-surface));
    border-radius: 28px;
    padding: 24px;
    box-shadow:
      0 6px 10px 0 rgba(0,0,0,0.30),
      0 12px 24px 8px rgba(0,0,0,0.15);
    animation: status-dlg-in 220ms cubic-bezier(.2,.0,0,1);
  }
  .status-dlg-title {
    font-size: 24px;
    line-height: 32px;
    font-weight: 400;
    margin: 0 0 20px 0;
  }
  .status-dlg-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 0.75rem;
  }
  .status-dlg-wide { grid-column: 1 / -1; }
  .status-dlg-hint {
    margin: -0.25rem 0 0;
    font-size: 0.75rem;
    color: rgb(var(--md-sys-color-on-surface-variant));
  }
  .status-dlg-check {
    display: flex;
    align-items: flex-start;
    gap: 0.625rem;
    cursor: pointer;
    font-size: 0.9rem;
    color: rgb(var(--md-sys-color-on-surface));
  }
  .status-dlg-checkbox {
    inline-size: 18px;
    block-size: 18px;
    margin-block-start: 2px;
    accent-color: rgb(var(--md-sys-color-primary));
    cursor: pointer;
    flex-shrink: 0;
  }
  .status-dlg-check-sub {
    display: block;
    font-size: 0.75rem;
    color: rgb(var(--md-sys-color-on-surface-variant));
    margin-block-start: 2px;
  }
  .status-dlg-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-block-start: 24px;
  }
  .status-dlg-btn {
    height: 40px;
    padding: 0 16px;
    border: 0;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
    color: rgb(var(--md-sys-color-primary));
    background: transparent;
    cursor: pointer;
  }
  .status-dlg-btn:hover { background: rgb(var(--md-sys-color-primary) / 0.08); }
  .status-dlg-btn-primary {
    padding: 0 24px;
    background: rgb(var(--md-sys-color-primary));
    color: rgb(var(--md-sys-color-on-primary));
  }
  .status-dlg-btn-primary:hover { background: rgb(var(--md-sys-color-primary)); opacity: 0.9; }

  @keyframes status-scrim-in { from { opacity: 0; } to { opacity: 1; } }
  @keyframes status-dlg-in {
    from { opacity: 0; transform: scale(0.96); }
    to   { opacity: 1; transform: scale(1); }
  }

  /* Responsive but capped at 4 columns on wide desktops (6 felt cramped).
     The max() floor makes each column at least 260px but never lets more
     than 4 fit: (100% - 3 gaps) / 4 is the 4-column width. */
  .status-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(max(260px, (100% - 3 * 0.75rem) / 4), 1fr));
    gap: 0.75rem;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  /* Group sections stack down the page; each has a small header with a count. */
  .status-group + .status-group { margin-block-start: 1.5rem; }
  .status-group-head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0 0 0.625rem;
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: rgb(var(--md-sys-color-on-surface-variant));
  }
  .status-group-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-inline-size: 20px;
    block-size: 20px;
    padding-inline: 6px;
    border-radius: 999px;
    background: rgb(var(--md-sys-color-surface-variant));
    color: rgb(var(--md-sys-color-on-surface-variant));
    font-size: 12px;
    font-weight: 600;
  }
  .status-card {
    background: rgb(var(--md-sys-color-surface-container));
    border-radius: 16px;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .status-card.disabled { opacity: 0.6; }
  .status-card-head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
  .status-card-name { font-size: 16px; font-weight: 500; margin: 0; flex: 1; min-inline-size: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  /* Heading reads as a link without screaming for attention: inherits the
     heading color until hover, when it shifts to primary to confirm it's
     interactive. The whole row stays one clean line. */
  .status-card-name a {
    color: inherit;
    text-decoration: none;
  }
  .status-card-name a:hover {
    color: rgb(var(--md-sys-color-primary));
    text-decoration: underline;
  }
  .status-card-paused {
    font-size: 11px;
    padding: 2px 6px;
    border-radius: 4px;
    background: rgb(var(--md-sys-color-surface-variant));
    color: rgb(var(--md-sys-color-on-surface-variant));
  }
  .status-card-actions { display: flex; gap: 0.25rem; }
  .status-icon-btn {
    inline-size: 32px;
    block-size: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    color: rgb(var(--md-sys-color-on-surface-variant));
    border: 0;
    border-radius: 50%;
    cursor: pointer;
  }
  .status-icon-btn:hover { background: rgb(var(--md-sys-color-on-surface) / 0.08); }
  .status-icon-btn .material-symbols-outlined { font-size: 18px; }

  /* URL line reads as a link by default (primary color + dotted underline),
     not just on hover. The external-link icon makes the "opens in new tab"
     affordance obvious without the user having to mouse over it first. */
  .status-card-url {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-family: 'Menlo', 'Consolas', monospace;
    font-size: 12px;
    color: rgb(var(--md-sys-color-primary));
    text-decoration: underline;
    text-decoration-style: dotted;
    text-underline-offset: 2px;
    word-break: break-all;
    inline-size: fit-content;
    max-inline-size: 100%;
  }
  .status-card-url:hover { text-decoration-style: solid; }
  .status-card-url-icon { font-size: 14px; flex-shrink: 0; }

  .status-card-meta {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.25rem 0.75rem;
    margin: 0;
    font-size: 12px;
  }
  .status-card-meta div { display: flex; justify-content: space-between; gap: 0.5rem; }
  .status-card-meta dt { color: rgb(var(--md-sys-color-on-surface-variant)); }
  .status-card-meta dd { margin: 0; font-variant-numeric: tabular-nums; }

  .status-card-error {
    margin: 0;
    padding: 0.375rem 0.5rem;
    background: rgb(var(--md-sys-color-error) / 0.08);
    color: rgb(var(--md-sys-color-error));
    border-radius: 6px;
    font-size: 12px;
    word-break: break-word;
  }

  /* Final URL after a redirect chain. */
  .status-card-final {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-family: 'Menlo', 'Consolas', monospace;
    font-size: 11px;
    color: rgb(var(--md-sys-color-on-surface-variant));
    text-decoration: none;
    inline-size: fit-content;
    max-inline-size: 100%;
  }
  .status-card-final:hover { color: rgb(var(--md-sys-color-primary)); }
  .status-card-final-text { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
</style>
